import { useEffect, useRef, useState } from 'react';
import { CometChat } from '@cometchat/chat-sdk-javascript';
import { CometChatMessageEvents, CometChatUIEvents } from '@cometchat/chat-uikit-react';
import DraftService from '../services/draft.service';

const useDraftMessages = (containerRef) => {
  const [currentDraft, setCurrentDraft] = useState('');
  const [currentConversation, setCurrentConversation] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [currentGroup, setCurrentGroup] = useState(null);

  // Use refs for values that need to be accessed in cleanup
  const currentDraftRef = useRef(currentDraft);
  const currentConversationRef = useRef(currentConversation);
  const currentUserRef = useRef(currentUser);
  const currentGroupRef = useRef(currentGroup);

  // Update refs when state changes
  useEffect(() => {
    currentDraftRef.current = currentDraft;
  }, [currentDraft]);

  useEffect(() => {
    currentConversationRef.current = currentConversation;
    currentUserRef.current = currentUser;
    currentGroupRef.current = currentGroup;
  }, [currentConversation, currentUser, currentGroup]);

  // const saveDraftForCurrentConversation = () => {
  //   const draft = currentDraftRef.current;
  //   if (!draft.trim()) return;

  //   const user = currentConversationRef.current?.getConversationType() === "user"
  //     ? currentConversationRef.current.getConversationWith()
  //     : currentUserRef.current;
  //   const group = currentConversationRef.current?.getConversationType() === "group"
  //     ? currentConversationRef.current.getConversationWith()
  //     : currentGroupRef.current;

  //   if (user) {
  //     DraftService.saveDraftForUser(user.getUid(), draft);
  //   } else if (group) {
  //     DraftService.saveDraftForGroup(group.getGuid(), draft);
  //   }
  // };

  const loadDraftForConversation = (conversation, user, group) => {
    const targetUser = conversation?.getConversationType() === "user"
      ? conversation.getConversationWith()
      : user;
    const targetGroup = conversation?.getConversationType() === "group"
      ? conversation.getConversationWith()
      : group;

    let draft = "";
    if (targetUser) {
      draft = DraftService.getDraftForUser(targetUser.getUid());
    } else if (targetGroup) {
      draft = DraftService.getDraftForGroup(targetGroup.getGuid());
    }

    setCurrentDraft(draft);

    // Set the draft in the contenteditable div
    setTimeout(() => {
      if (!containerRef.current) return;

      const contentEditableDiv = containerRef.current.querySelector('div[contenteditable]');
      if (contentEditableDiv && draft) {
        contentEditableDiv.textContent = draft;
        contentEditableDiv.dispatchEvent(new Event('input', { bubbles: true }));
        contentEditableDiv.focus();
      }
    }, 100);
  };

  // Listen for CometChat active chat changes
  useEffect(() => {
    const chatChanged = CometChatUIEvents.ccActiveChatChanged?.subscribe?.((activeChat) => {
      if (activeChat) {
        // Save current draft before switching
        // saveDraftForCurrentConversation();

        // Update current conversation/user/group
        if (activeChat.conversation) {
          setCurrentConversation(activeChat.conversation);
          setCurrentUser(null);
          setCurrentGroup(null);
          loadDraftForConversation(activeChat.conversation, null, null);
        } else if (activeChat.user) {
          setCurrentConversation(null);
          setCurrentUser(activeChat.user);
          setCurrentGroup(null);
          loadDraftForConversation(null, activeChat.user, null);
        } else if (activeChat.group) {
          setCurrentConversation(null);
          setCurrentUser(null);
          setCurrentGroup(activeChat.group);
          loadDraftForConversation(null, null, activeChat.group);
        }
      }
    });

    return () => {
      chatChanged?.unsubscribe?.();
    };
  }, []); // Empty dependency array since we use refs

  // Listen for message sent events
  useEffect(() => {
    const handleMessageSent = (data) => {
      if (data.message?.receiverType === "group") {
        DraftService.clearDraftForGroup(data.message?.receiver?.guid);
        setCurrentDraft('');
      } else if (data.message?.receiverType === "user") {
        DraftService.clearDraftForUser(data.message?.receiver?.uid);
        setCurrentDraft('');
      }
    };

    const sentMessageSubscription = CometChatMessageEvents.ccMessageSent.subscribe(handleMessageSent);

    return () => {
      sentMessageSubscription.unsubscribe();
    };
  }, []);

  // Listen for message input changes
  useEffect(() => {
    if (!containerRef.current) return;

    let isUpdatingFromSync = false;

    const handleInputChange = (e) => {
      if (e.target.contentEditable === 'true' || e.target.contentEditable === 'plaintext-only') {
        const loadingOverlay = containerRef.current?.querySelector('[style*="Loading messages"]');
        if (loadingOverlay) {
          e.preventDefault();
          return;
        }

        // Skip if this is an update from sync to prevent infinite loops
        if (isUpdatingFromSync) {
          return;
        }

        const text = e.target.textContent || e.target.innerText || '';
        setCurrentDraft(text);

        // Debounce draft saving
        setTimeout(() => {
          const user = currentConversationRef.current?.getConversationType() === "user"
            ? currentConversationRef.current.getConversationWith()
            : currentUserRef.current;
          const group = currentConversationRef.current?.getConversationType() === "group"
            ? currentConversationRef.current.getConversationWith()
            : currentGroupRef.current;

          if (user) {
            DraftService.saveDraftForUser(user.getUid(), text);
          } else if (group) {
            DraftService.saveDraftForGroup(group.getGuid(), text);
          }
        }, 300);
      }
    };

    const handlePaste = (e) => {
      if (e.target.contentEditable === 'true' || e.target.contentEditable === 'plaintext-only') {
        // Use setTimeout to get the text after paste is processed
        setTimeout(() => {
          handleInputChange(e);
        }, 0);
      }
    };

    const handleDraftSync = (e) => {
      const { key, newValue } = e.detail;

      const user = currentConversationRef.current?.getConversationType() === "user"
        ? currentConversationRef.current.getConversationWith()
        : currentUserRef.current;
      const group = currentConversationRef.current?.getConversationType() === "group"
        ? currentConversationRef.current.getConversationWith()
        : currentGroupRef.current;

      let isForCurrentConversation = false;
      if (user && key === `cometchat_draft_user_${user.getUid()}`) {
        isForCurrentConversation = true;
      } else if (group && key === `cometchat_draft_group_${group.getGuid()}`) {
        isForCurrentConversation = true;
      }

      if (isForCurrentConversation && containerRef.current) {
        const contentEditableDiv = containerRef.current.querySelector('div[contenteditable]');
        if (contentEditableDiv) {
          const currentText = contentEditableDiv.textContent || contentEditableDiv.innerText || '';
          const targetText = newValue || '';

          if (currentText !== targetText && document.activeElement !== contentEditableDiv) {
            isUpdatingFromSync = true;

            const selection = window.getSelection();
            const range = selection.rangeCount > 0 ? selection.getRangeAt(0) : null;
            const cursorOffset = range ? range.startOffset : 0;

            contentEditableDiv.textContent = targetText;
            setCurrentDraft(targetText);

            if (range && contentEditableDiv.firstChild) {
              const newRange = document.createRange();
              const textNode = contentEditableDiv.firstChild;
              const maxOffset = Math.min(cursorOffset, textNode.textContent.length);
              newRange.setStart(textNode, maxOffset);
              newRange.setEnd(textNode, maxOffset);
              selection.removeAllRanges();
              selection.addRange(newRange);
            }

            setTimeout(() => {
              isUpdatingFromSync = false;
            }, 100);
          }
        }
      }
    };

    const handleKeyDown = (e) => {
      const loadingOverlay = containerRef.current?.querySelector('[style*="Loading messages"]');
      if (loadingOverlay && (e.target.contentEditable === 'true' || e.target.contentEditable === 'plaintext-only')) {
        e.preventDefault();
      }
    };

    const handleSendClick = (e) => {
      const loadingOverlay = containerRef.current?.querySelector('[style*="Loading messages"]');
      if (loadingOverlay && (e.target.contentEditable === 'true' || e.target.contentEditable === 'plaintext-only')) {
        e.preventDefault();
      }

      // const user = currentConversationRef.current?.getConversationType() === "user"
      //   ? currentConversationRef.current.getConversationWith()
      //   : currentUserRef.current;
      // const group = currentConversationRef.current?.getConversationType() === "group"
      //   ? currentConversationRef.current.getConversationWith()
      //   : currentGroupRef.current;

      // if (user) {
      //   DraftService.clearDraftForUser(user.getUid());
      // } else if (group) {
      //   DraftService.clearDraftForGroup(group.getGuid());
      // }
      // setCurrentDraft("");
    };

    const container = containerRef.current;
    container.addEventListener('input', handleInputChange, true);
    container.addEventListener('paste', handlePaste, true);
    container.addEventListener('keydown', handleKeyDown, true);
    container.addEventListener('click', handleSendClick, true);
    window.addEventListener('draftChanged', handleDraftSync);

    return () => {
      container.removeEventListener('input', handleInputChange, true);
      container.removeEventListener('paste', handlePaste, true);
      container.removeEventListener('keydown', handleKeyDown, true);
      container.removeEventListener('click', handleSendClick, true);
      window.removeEventListener('draftChanged', handleDraftSync);
    };
  }, [containerRef]);

  return {
    currentDraft,
    loadDraftForConversation
  };
};

export default useDraftMessages;